import { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Layout, Collapse, Form, Button, Divider } from 'antd';
import { ChevronUp, ChevronDown, Eye, Sun, Moon } from 'react-feather';
import { useNavigate } from 'react-router-dom';

import {
  Logo,
  TargetSettingsPanel,
  DesignSettingsPanel,
  ContentSkeleton,
  IntegrationModal,
} from '../../components';

import {
  decrypt,
  getUserProperties,
  handleInitilazionData,
  handleStoryProductAdded,
  rebuildStorylyDOM,
} from '../../utils';
import './style.scss';
import { storylyInitialValues } from '../../constants';

const { Header, Sider, Content } = Layout;

const Preview = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const tokenParam = urlParams.get('data');
  const decrypted = decrypt('test', tokenParam);
  const [token, segments] = (decrypted || '').split('|');
  const storylyRef = useRef();
  const navigate = useNavigate();
  const storylyContainerRef = useRef();
  const [form] = Form.useForm();

  const [userProperties, setUserProperties] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const borderUnseenColor = Form.useWatch(
    ['props', 'storyGroupIconBorderColorNotSeen'],
    { form, preserve: true }
  );
  const borderSeenColor = Form.useWatch(
    ['props', 'storyGroupIconBorderColorSeen'],
    { form, preserve: true }
  );

  // useEffect(() => {
  //   storylyRef.current?.setProperties({
  //     storyGroupIconBorderColorNotSeen: borderUnseenColor,
  //   });
  // }, [borderUnseenColor]);

  // useEffect(() => {
  //   storylyRef.current?.setProperties({
  //     storyGroupIconBorderColorSeen: borderSeenColor,
  //   });
  // }, [borderSeenColor]);

  useLayoutEffect(() => {
    const storylyScript = document.createElement('script');
    if (!document.querySelector('#storyly-web-script')) {
      storylyScript.setAttribute('custom-element', 'storyly-web');
      storylyScript.setAttribute('id', 'storyly-web-script');
      storylyScript.setAttribute(
        'src',
        `https://web-story.storyly.io/sdk/4.*/storyly-web.js`
      );
      document.body.appendChild(storylyScript);
    }

    if (token) {
      storylyScript.onload = () => {
        setScriptLoaded(true);
        rebuildStorylyDOM(storylyRef, storylyContainerRef);

        storylyRef.current.init({
          ...handleInitilazionData({
            token,
            // ...storylyInitialValues,
          }),
        });

        storylyRef.current.on('loaded', (data) => {
          setUserProperties(getUserProperties(data.allGroups));
        });

        storylyRef.current.on('onStoryCartUpdate', handleStoryProductAdded);
      };
    } else {
      navigate('/');
    }
  }, []);

  // useEffect(() => {
  //   if (scriptLoaded) {
  //     storylyRef.current.setProperties({
  //       storyGroupTextColor: darkMode ? '#FFFFFF' : '#4A4A4A',
  //     });
  //   }
  // }, [darkMode]);

  const onFormSubmit = (data) => {
    rebuildStorylyDOM(storylyRef, storylyContainerRef);

    if (data.videoFeedView === 'grid') {
      data = {
        ...data,
        props: {
          ...data.props,
          scrollIndicator: undefined,
        },
      };
    }

    storylyRef.current.init({
      ...handleInitilazionData({
        token,
        ...data,
      }),
    });

    storylyRef.current.on('loaded', (data) => {
      setUserProperties(getUserProperties(data.allGroups));
    });

    storylyRef.current.on('storyCartUpdate', handleStoryProductAdded);
  };

  const onFormChange = (data) => {
    const formValues = form.getFieldsValue();

    if (data.props) {
      const mergedData = { ...formValues.props, ...data.props };

      const {
        storyGroupSizeWidth,
        storyGroupSizeHeight,
        storyGroupBorderSize,
        storyGroupAnimation,
        scrollIndicator,
      } = mergedData;

      storylyRef.current?.setProperties({
        ...mergedData,
        scrollIndicator: scrollIndicator === 'on',
        storyGroupSize: `${storyGroupSizeWidth}x${storyGroupSizeHeight}`,
        storyGroupBorderSize: +storyGroupBorderSize,
        storyGroupAnimation: storyGroupAnimation ? { type: 'pulse' } : false,
      });
    }
  };

  const rightMenu = [
    {
      key: '2',
      label: 'Target Settings',
      children: (
        <TargetSettingsPanel
          segments={segments}
          form={form}
          userProperties={userProperties}
        />
      ),
      forceRender: true,
    },
    {
      key: '3',
      label: 'Design Settings',
      children: <DesignSettingsPanel form={form} />,
      forceRender: true,
    },
  ];

  return (
    <Layout className="preview-wrapper">
      <Header className="preview-wrapper-header">
        <Logo />
        <h2>
          <span className="preview-wrapper-header-eye">
            <Eye size={16} />
          </span>
          Preview Mode
        </h2>
        <div className="preview-wrapper-header-right">
          <div className="preview-wrapper-header-right">
            <div
              className="preview-wrapper-header-right-mode"
              onClick={() => setDarkMode(!darkMode)}
            >
              {darkMode ? <Moon size={24} /> : <Sun size={24} />}
            </div>
            <Divider type="vertical" />
            <Button size="large" onClick={() => setModalOpen(true)}>
              Scan QR / Copy
            </Button>
          </div>
        </div>
      </Header>
      <Layout
        className="preview-layout"
        style={{
          height: 'calc(100vh - 64px)',
        }}
      >
        <Content style={{ background: darkMode ? '#0A0A0A' : 'initial' }}>
          <div
            style={{
              width: form.getFieldsValue().props?.storyPreviewWidth
                ? `${form.getFieldsValue().props?.storyPreviewWidth}px`
                : '100%',
            }}
            ref={storylyContainerRef}
          ></div>
          <ContentSkeleton darkMode={darkMode} />
        </Content>
        <Sider width={369}>
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={onFormSubmit}
            onValuesChange={onFormChange}
            initialValues={storylyInitialValues}
          >
            <Collapse
              accordion
              defaultActiveKey="1"
              ghost
              expandIconPosition="end"
              expandIcon={({ isActive }) => {
                return isActive ? <ChevronUp /> : <ChevronDown />;
              }}
              items={rightMenu}
            />
            <div className="test-env-wrapper-buttons">
              <Form.Item noStyle>
                <Button
                  size="large"
                  htmlType="button"
                  onClick={() => {
                    onFormSubmit(storylyInitialValues);
                    form.setFieldsValue(storylyInitialValues);
                  }}
                >
                  Reset Settings
                </Button>
                <Button type="primary" size="large" htmlType="submit">
                  Apply Changes
                </Button>
              </Form.Item>
            </div>
          </Form>
        </Sider>
      </Layout>
      <IntegrationModal
        open={modalOpen}
        setOpen={setModalOpen}
        form={form}
        token={token}
      />
    </Layout>
  );
};

export default Preview;
